import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import {
  Article,
  Breadcrumb,
  CtaSimulacao,
  Sidebar,
} from "../../../components";
import type Post from "../../../types/Post";
import { CurrentPost, PostAdsenseIds } from "../../../graphql/post";
import styles from "./post.module.scss";
import Categories from "../../../types/Categories";
import { LastPost } from "../../../types/LastPost";
import { ItemsExpansives } from "../../../types/ExpansiveItems";
import { useWindowSize } from "../../../utils/useWindowSize";
import { itemsMenuHierarch } from "../../../utils/menuHierarch";
import { FooterWidget, NewMenu } from "../../../types/Menu";
import { PathPosts } from "../../../graphql/pathPosts";
import { PathPostsTypes } from "../../../types/PathPosts";
import { executeQuery } from "../../../services/graphql";
import MainLayout from "../../../components/MainLayout";
import { CACHE_TIME_DEFAULT } from "../../../utils/requests";
import { getFooterMenu } from "../../../repositories/menu";
import { PopupTypes } from "../../../types/Popup";
import {
  PostAdsenseIdsType,
  PostQueryRequest,
} from "../../../services/graphql/types/postQuery";

interface PostsQueryResult {
  menu: Array<NewMenu>;
  Post: Post;
  LastPost: LastPost;
  expansivePosts: Array<ItemsExpansives>;
  expansivePages: Array<ItemsExpansives>;
  categories: Array<Categories>;
  footerMenu: FooterWidget;
  popupData?: PopupTypes;
  adSenseIds: PostAdsenseIdsType["optionsPage"]["opcoes"]["postids"];
}

const PostPage: NextPage<PostsQueryResult> = ({
  menu,
  Post,
  LastPost,
  expansivePosts,
  expansivePages,
  categories,
  footerMenu,
  popupData,
  adSenseIds,
}) => {
  const size = useWindowSize();
  return (
    <MainLayout
      headerMenu={menu}
      head={{
        customHeadData: Post.seo.fullHead,
      }}
      footerMenu={footerMenu}
      popupData={popupData}
    >
      <div className={styles.containerPost}>
        <div className={styles.articleContainer}>
          <Breadcrumb
            title={Post.title}
            slugCat={Post.categories.nodes[0].slug}
            category={Post.categories.nodes[0].name}
          />
          <div className={styles.articleRow}>
            <Article
              items={Post}
              lastPost={LastPost}
              indexAdsenseId={adSenseIds.postIndex}
              contentAdsenseId={adSenseIds.postContent}
            />
            <Sidebar
              categoriesBlock={categories}
              expansorPosts={expansivePosts}
              expansorPages={expansivePages}
              adSenseId={adSenseIds.aside}
            />
          </div>
        </div>
      </div>
      {size.width < 992 && <CtaSimulacao />}
    </MainLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await executeQuery<PostQueryRequest>(PathPosts);

  const post = data?.posts.nodes;

  const paths = post.map((post: PathPostsTypes) => {
    return {
      params: {
        slug: post.slug,
      },
    };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params.slug as string;

  const { data } = await executeQuery<PostQueryRequest>(CurrentPost, {
    slugPost: slug || "",
    qtdPagesExpansor: 50,
  });

  const { data: adsData } = await executeQuery<PostAdsenseIdsType>(
    PostAdsenseIds
  );

  const Post = data?.post;
  const LastPost = data?.post?.previous;
  const categories = data?.categories?.nodes;
  const expansivePosts = data?.UltimasNoticiasExpansivo?.nodes;
  const expansivePages = data?.PaginasExpansivo?.nodes;
  const popupData = data?.optionsPage?.opcoes;

  const menu = itemsMenuHierarch(data?.Head?.nodes[0]?.menuItems?.nodes);
  const footerMenu = await getFooterMenu();

  if (!data || !Post) {
    return { notFound: true };
  }

  return {
    props: {
      menu,
      Post,
      LastPost,
      expansivePosts,
      expansivePages,
      categories,
      footerMenu,
      popupData,
      adSenseIds: adsData?.optionsPage?.opcoes?.postids,
    },
    revalidate: CACHE_TIME_DEFAULT,
  };
};

export default PostPage;
